import React, { useState, useEffect } from "react";
import { Box, Grid, Typography, makeStyles } from "@material-ui/core";
import { useHistory, useLocation } from "react-router-dom";
import { fbroot } from "../../firebase";
import ScrollToTop from "../../components/ScrollToTop";
import Filters from "./components/filters";
import Sorting from "./components/sorting";
import AlbumList from "../../components/AlbumList";
import UploaderInfo from "../../components/UploaderInfo";
import useConfig from "../../hooks/useConfig";
import { keys, values } from "lodash";

const db = fbroot.database();

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "auto",
    marginTop: theme.spacing(10),
    marginBottom: theme.spacing(5),
    maxWidth: "90vw",
  },
  layout: {
    display: "flex",
    flexDirection: "row",
    [theme.breakpoints.down('sm')]: {
      flexDirection: "column",
    },
  },
  uploaderColumn: {
    width: 250,
    flexShrink: 0,
    marginRight: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginRight: 0,
      marginBottom: theme.spacing(3),
    },
  },
  albumsContainer: {
    flexGrow: 1,
    overflow: 'hidden',
  },
  heading: {
    marginBottom: theme.spacing(3),
    zIndex: 2,
    position: "relative",
    [theme.breakpoints.up("md")]: {
      paddingTop: theme.spacing(4),
    },
  },
  filters: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    justifyContent: "space-between",
    [theme.breakpoints.up("md")]: {
      justifyContent: "flex-end",
    },
  },
  title: {
    whiteSpace: "nowrap",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.5rem",
    },
  },
}));

const AlbumsAdmin = () => {
  const classes = useStyles();
  const config = useConfig();
  const location = useLocation();
  
  const [loading, setLoading] = useState(false);
  const [albums, setAlbums] = useState({});
  const [filter, setFilter] = useState("all");
  const [filterCount, setFilterCount] = useState({ all: 0 }); // Initialize with all: 0
  const [sort, setSort] = useState("date");
  const [message, setMessage] = useState("");
  const [uploader, setUploader] = useState("");
  
  // Initialize from URL query
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlQuery = searchParams.get("q");

    console.log("AlbumsAdmin rendered");
    
    if (urlQuery) {
      handleSearch(urlQuery);
    } else {
      // If no search query, fetch recent albums
      fetchRecentAlbums();
    }
  }, [location.search]);

  // Calculate filter counts whenever albums change
  useEffect(() => {
    if (Object.keys(albums).length > 0) {
      console.log("Calculating filter counts from", Object.keys(albums).length, "albums");
      
      // Calculate filter counts based on album statuses
      const counts = values(albums).reduce(
        (totals, album) => {
          const category = getStatusCategory(album.status);
          
          if (!totals[category]) totals[category] = 0;
          
          totals[category] += 1;
          totals.all += 1;
          
          return totals;
        },
        { all: 0 }
      );
      
      console.log("Filter counts calculated:", counts);
      
      // Update filter counts
      setFilterCount(counts);
    }
  }, [albums]);

  // Fetch recent albums
  const fetchRecentAlbums = () => {
    setLoading(true);
    setAlbums({});
    setMessage("");
    setUploader("");
    
    // Clear previous listeners
    db.ref("catalog").off();
    
    // Fetch the most recent albums by timestamp
    db.ref("catalog")
      .orderByChild("timestamps/ingested")
      .limitToLast(200) // Limit to the 20 most recent albums
      .on("child_added", (snap) => {
        setAlbums(prev => ({
          ...prev,
          [snap.key]: snap.val()
        }));
        setLoading(false);
      });
      
    db.ref("catalog")
      .orderByChild("timestamps/ingested")
      .limitToLast(200)
      .on("child_changed", (snap) => {
        setAlbums(prev => ({
          ...prev,
          [snap.key]: snap.val()
        }));
      });
      
    // Set a timeout to stop loading state if no results come back
    setTimeout(() => {
      setLoading(false);
    }, 5000);
  };

  // Perform search
  const handleSearch = (searchQuery) => {
    if (!searchQuery) {
      fetchRecentAlbums();
      return;
    }
    
    setLoading(true);
    setAlbums({});
    setMessage("");
    
    // Determine search type and field
    let field, value;
    
    switch (true) {
      // email search
      case searchQuery.includes("@"):
        field = "subaccount_user";
        value = searchQuery;
        break;

      // UPC search
      case /^\d{13}$/.test(searchQuery):
        field = "upc";
        value = searchQuery;
        break;

      // field:value search
      case searchQuery.includes(":"):
        [field, value] = searchQuery.split(":").map((x) => x.trim());
        break;

      // isrc search
      case /^[A-Z]{2}\d{10}$/.test(searchQuery):
        field = "isrc";
        value = searchQuery;
        break;
        
      default:
        setMessage("Formato de búsqueda no reconocido");
        setLoading(false);
        return;
    }

    // Convert email field name if needed
    if (field === "email") {
      field = "subaccount_user";
    }

    // Set uploader for UploaderInfo component
    if (field === "subaccount_user") {
      setUploader(value);
    } else {
      setUploader("");
    }

    // Perform search based on field type
    if (field && value) {
      // Clear previous listeners
      db.ref("catalog").off();
      
      if (field === "isrc") {
        db.ref("isrcToUpc")
          .child(value)
          .once("value", (snap) => {
            if (snap.exists()) {
              const upc = snap.val();
              db.ref("catalog")
                .child(upc)
                .once("value", (snap) => {
                  if (snap.exists()) {
                    setAlbums({ [snap.key]: snap.val() });
                  } else {
                    setMessage("No se encontraron resultados");
                  }
                  setLoading(false);
                });
            } else {
              setMessage("No se encontraron resultados");
              setLoading(false);
            }
          });
      } else {
        // Regular field search
        db.ref("catalog")
          .orderByChild(field)
          .equalTo(value)
          .on("child_added", (snap) => {
            setAlbums(prev => ({
              ...prev,
              [snap.key]: snap.val()
            }));
            setLoading(false);
          });

        db.ref("catalog")
          .orderByChild(field)
          .equalTo(value)
          .on("child_changed", (snap) => {
            setAlbums(prev => ({
              ...prev,
              [snap.key]: snap.val()
            }));
          });

        // If searching by subaccount_user (email), also search by subaccount
        if (field === "subaccount_user") {
          db.ref("catalog")
            .orderByChild("subaccount")
            .equalTo(value)
            .on("child_added", (snap) => {
              setAlbums(prev => ({
                ...prev,
                [snap.key]: snap.val()
              }));
              setLoading(false);
            });

          db.ref("catalog")
            .orderByChild("subaccount")
            .equalTo(value)
            .on("child_changed", (snap) => {
              setAlbums(prev => ({
                ...prev,
                [snap.key]: snap.val()
              }));
            });
        }
        
        // Set a timeout to stop loading state if no results come back
        setTimeout(() => {
          setLoading(false);
        }, 5000);
      }
    } else {
      setMessage("Búsqueda inválida");
      setLoading(false);
    }
  };

  // Handle filter change
  const handleFilter = (choice) => {
    setFilter(choice);
  };

  // Handle sort change
  const handleSort = (choice) => {
    setSort(choice);
  };

  // Get status category for filtering with debug logging
  const getStatusCategory = (status) => {
    const category = config.statuses[status]?.category || "other";
    // Uncomment for debugging
    // console.log(`Status ${status} maps to category ${category}`);
    return category;
  };

  return (
    <div className={classes.root}>
      <ScrollToTop />

      {/* Title, filters and sorting */}
      <div className={classes.heading}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography className={classes.title} variant="h4">
              {keys(albums).length || 0} ÁLBUMS
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Box className={classes.filters}>
              <Filters
                selected={filter}
                filterCount={filterCount}
                onSelect={handleFilter}
              />
              <Sorting selected={sort} onSelect={handleSort} />
            </Box>
          </Grid>
        </Grid>
      </div>

      {/* Search results */}
      <Box className={classes.layout}>
        {uploader && (
          <Box className={classes.uploaderColumn}>
            <UploaderInfo email={uploader} />
          </Box>
        )}

        <Box style={{ flexGrow: 1 }}>
          {message && !loading && (
            <Typography variant="body1" style={{ marginBottom: 16 }}>
              {message}
            </Typography>
          )}

          <AlbumList
            albums={albums}
            loading={loading}
            filter={filter}
            sort={sort}
            getStatusCategory={getStatusCategory}
            initialPageSize={12}
            incrementSize={12}
          />
        </Box>
      </Box>
    </div>
  );
};

export default AlbumsAdmin;
